import { ReactNode } from 'react';
import { tracking } from '@iwoca/frontend-tracking-library';
import { t } from '@lingui/core/macro';
import { Trans } from '@lingui/react/macro';
import { TDateUnit } from 'lendingApi/applications.types';
import { TDataRequirementAction } from 'lendingApi/lendingApi.types';
import { TFileStatusGroup } from 'types';
import { getAmplitudeDefaultEventProperties } from 'utils/amplitudeEvents';
import { getDurationInMonths } from 'utils/durationHelper';
import { DE_LOCALE } from 'utils/i18n';

export const FILE_STATUS_GROUPS: TFileStatusGroup[] = [
  'uploaded',
  'rejected',
  'approved',
  'overridden',
];

export const getRequirementsTitle = (type: TDataRequirementAction['type']) => {
  switch (type) {
    case 'management_accounts':
      return t`Current management accounts`;
    case 'management_and_balance_accounts':
      return t`Management and balance accounts`;
    case 'year_end_management_accounts':
      return t`Year End Management Accounts`;
    case 'year_end_management_and_balance_accounts':
      return t`Year end Management and balance accounts`;
    case 'share_registry':
      return t`Share registry`;
    case 'articles_of_association':
      return t`Articles of association`;
    case 'trade_certificate':
      return t`Trade certificate`;
    case 'vat_return':
      return t`VAT returns`;
    case 'bank_statement':
      return t`Bank statements`;
    case 'business_plan':
      return 'Business plan';
    case 'aged_debtor_report':
      return 'Aged debtor report';
    case 'business_accounts':
      return 'Company accounts';

    default:
      break;
  }
};

export const getInstruction = (
  documentType: string | undefined,
  quantifier?: number | undefined,
  quantifier_unit?: TDateUnit | undefined,
) => {
  const monthsDuration = getDurationInMonths({
    amount: quantifier as number,
    unit: quantifier_unit as TDateUnit,
  });

  // Added below logic due to some quanitifers coming back as 12 months and some 1 year, so we want to keep them aligned.
  const is12Months = quantifier && monthsDuration === 12;

  const showYearValue =
    (quantifier && quantifier_unit === 'years') || is12Months;

  switch (documentType) {
    case 'business_accounts':
      if (showYearValue) {
        return quantifier > 1 && !is12Months
          ? t`Please upload ${quantifier} years of your filed company accounts`
          : t`Please upload 1 year of your filed company accounts`;
      }

      if (monthsDuration) {
        return quantifier && monthsDuration > 1
          ? t`Please upload ${quantifier} months of your filed company accounts`
          : t`Please upload 1 month of your filed company accounts`;
      }

      return t`Please upload your filed company accounts`;
    case 'management_accounts':
      if (showYearValue) {
        return quantifier > 1 && !is12Months
          ? t`Please upload ${quantifier} years of your management accounts`
          : t`Please upload 1 year of your management accounts`;
      }

      if (monthsDuration) {
        return quantifier && monthsDuration > 1
          ? t`Please upload ${quantifier} months of your management accounts`
          : t`Please upload 1 month of your management accounts`;
      }

      return t`Please upload your management accounts`;
    case 'business_plan':
      if (showYearValue) {
        return quantifier > 1 && !is12Months
          ? t`Please upload ${quantifier} years of your business plan`
          : t`Please upload 1 year of your business plan`;
      }

      if (monthsDuration) {
        return quantifier && monthsDuration > 1
          ? t`Please upload ${quantifier} months of your business plan`
          : t`Please upload 1 month of your business plan`;
      }

      return t`Please upload your business plan`;
    case 'aged_debtor_report':
      if (showYearValue) {
        return quantifier > 1 && !is12Months
          ? t`Please upload ${quantifier} years of your aged debtor report`
          : t`Please upload 1 year of your aged debtor report`;
      }

      if (monthsDuration) {
        return quantifier && monthsDuration > 1
          ? t`Please upload ${quantifier} months of your aged debtor report`
          : t`Please upload 1 month of your aged debtor report`;
      }

      return t`Please upload your aged debtor report`;
    case 'vat_return':
      if (showYearValue) {
        return quantifier && quantifier > 1 && !is12Months
          ? t`Please upload ${quantifier} years of your VAT returns`
          : t`Please upload 1 year of your VAT returns`;
      }

      if (monthsDuration) {
        return quantifier && monthsDuration > 1
          ? t`Please upload ${quantifier} months of your VAT returns`
          : t`Please upload 1 month of your VAT returns`;
      }

      return t`Please upload your VAT returns`;
    case 'bank_statement':
      if (showYearValue) {
        return quantifier > 1 && !is12Months
          ? t`Please upload ${quantifier} years of your bank statements`
          : t`Please upload 1 year of your bank statements`;
      }

      if (monthsDuration) {
        return quantifier && monthsDuration > 1
          ? t`Please upload ${quantifier} months of your bank statements`
          : t`Please upload 1 month of your bank statements`;
      }

      return t`Please upload your bank statements`;

    case 'management_and_balance_accounts':
      return t`Please upload your management and balance accounts`;
    case 'year_end_management_and_balance_accounts':
      return t`Please upload your year end management and balance accounts`;
    case 'share_registry':
      return t`Please upload your share registry`;

    default:
      break;
  }
};

export const getAdditionalText = (
  documentType: TDataRequirementAction['type'],
): ReactNode => {
  switch (documentType) {
    case 'management_and_balance_accounts':
    case 'year_end_management_and_balance_accounts':
      return (
        <Trans>
          <strong>Tips</strong> for getting the SuSa right first time can be
          found{' '}
          <a
            href="https://www.iwoca.de/kredit/datev-guide"
            target="_blank"
            rel="noreferrer"
            onClick={() =>
              tracking.ampli.ctaClick({
                ...getAmplitudeDefaultEventProperties(),
                'CTA text': 'SuSa support',
              })
            }
          >
            here.
          </a>
        </Trans>
      );
    default:
      break;
  }
};

export type TDocumentTypeWithDE = keyof typeof documentTypeDEMapping;

const documentTypeDEMapping = {
  bwa_hochladen: 'management_accounts',
  kontoauszuege_hochladen: 'bank_statement',
  bwa_susa_hochladen: 'management_and_balance_accounts',
  jaehrliche_bwa_susa_hochladen: 'year_end_management_and_balance_accounts',
  aktienregisterauszug_hochladen: 'share_registry',
  gesellschaftsvertrag_hochladen: 'articles_of_association',
  gewerbeschein_hochladen: 'trade_certificate',
  company_accounts: 'company_accounts',
};

export const handleDocumentTypeMapping = (
  type: TDocumentTypeWithDE,
  locale: string,
) => {
  if (locale === DE_LOCALE) {
    if (documentTypeDEMapping[type]) {
      return documentTypeDEMapping[type];
    }
  } else {
    // This is because product_offers asks for "company_accounts"
    // But POST documents accepts it under "business accounts"
    return type === 'company_accounts' ? 'business_accounts' : type;
  }
  return '';
};
